import { Injectable } from '@angular/core';
import { Router, CanActivate } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of, race, timer } from 'rxjs';
import { filter, map, catchError, take, tap } from 'rxjs/operators';
import { ApiService } from '../services/api.service';
import { NotificationService } from '../services/notification.service';
import { I18nService } from '../services/i18n.service';

@Injectable({
  providedIn: 'root'
})
export class GuideFlowConfigGuard implements CanActivate {

  constructor(
    private router: Router,
    private apiService: ApiService,
    private notificationService: NotificationService,
    private i18n: I18nService
  ) {}

  // The route snapshot and router state are not part of the decision — the gate
  // is a single module-wide permission — so they are left off the signature
  // (`CanActivate` is satisfied by a narrower one, and unused parameters are a
  // lint error here).
  canActivate(): Observable<boolean> {
    return this.apiService.get<{ canAccess: boolean }>(
      'user-access/check',
      { permission: 'CONFIGURACOES_CADASTRO_FLUXO_GUIAS' }
    ).pipe(
      map(response => response.canAccess),
      tap(canAccess => {
        if (!canAccess) {
          this.denyAccess();
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (error?.status === 403) {
          this.denyAccess();
        } else {
          const cameFromWithinApp = !!this.router.getCurrentNavigation()?.previousNavigation;
          if (!cameFromWithinApp) {
            this.router.navigate(['/home']);
          }
        }
        return of(false);
      })
    );
  }

  private denyAccess(): void {
    const cameFromWithinApp = !!this.router.getCurrentNavigation()?.previousNavigation;
    if (!cameFromWithinApp) {
      this.router.navigate(['/home']);
    }
    // Wait for translations before showing toast — prevents raw-key display on fast navigation.
    // race with timer(3000) ensures the subscription self-cleans even if translationsReady$ never emits.
    race(
      this.i18n.translationsReady$.pipe(filter(ready => ready), take(1)),
      timer(3000)
    ).pipe(take(1)).subscribe(() => {
      this.notificationService.warning(
        this.i18n.translate('notifications.moduleGuideFlowConfig'),
        this.i18n.translate('notifications.accessDenied')
      );
    });
  }
}
